(() => {
  if (customElements.get('evilko-home-slider')) return;

  class EvilkoHomeSlider extends HTMLElement {
    connectedCallback() {
      if (this.running) return;
      this.slides = Array.from(this.querySelectorAll('[data-hero-slide]'));
      if (!this.slides.length) return;
      this.running = true;
      const controls = this.querySelector('[data-hero-controls]');
      if (controls) controls.hidden = this.slides.length < 2;
      this.indexButtons = Array.from(this.querySelectorAll('[data-hero-index]'));
      this.toggleButton = this.querySelector('[data-hero-toggle]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.intervalMs = Math.max(1000, Number(this.dataset.interval) || 5000);
      this.currentIndex = 0;
      this.userPaused = false;
      this.hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
      this.hovered = this.hoverQuery.matches && this.matches(':hover');
      this.focused = this.contains(document.activeElement);
      this.visible = true;

      this.onClick = event => {
        const button = event.target.closest('button[data-hero-index], button[data-hero-prev], button[data-hero-next], button[data-hero-toggle]');
        if (!button || !this.contains(button) || button.disabled) return;
        if (button.hasAttribute('data-hero-toggle')) {
          this.userPaused = !this.userPaused;
          this.refresh();
          return;
        }
        const requestedIndex = button.hasAttribute('data-hero-index')
          ? Number(button.dataset.heroIndex)
          : this.currentIndex + (button.hasAttribute('data-hero-prev') ? -1 : 1);
        this.navigate(requestedIndex);
      };
      this.onKeydown = event => {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
        if (event.target !== this && !event.target.matches('button[data-hero-index], button[data-hero-prev], button[data-hero-next]')) return;
        event.preventDefault();
        this.navigate(this.currentIndex + (event.key === 'ArrowLeft' ? -1 : 1));
      };
      this.onMouseEnter = () => { this.hovered = this.hoverQuery.matches; this.refresh(); };
      this.onMouseLeave = () => { this.hovered = false; this.refresh(); };
      this.onFocusIn = () => { this.focused = true; this.refresh(); };
      this.onFocusOut = event => {
        this.focused = Boolean(event.relatedTarget && this.contains(event.relatedTarget));
        this.refresh();
      };
      this.onPointerDown = event => {
        if (event.pointerType !== 'touch' || event.target.closest('a,button,input,select,textarea')) return;
        this.touchStart = {x: event.clientX, y: event.clientY};
      };
      this.onPointerUp = event => {
        if (!this.touchStart) return;
        const dx = event.clientX - this.touchStart.x;
        const dy = event.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) >= 60 && Math.abs(dy) < 40) this.navigate(this.currentIndex + (dx < 0 ? 1 : -1));
      };
      this.onPointerCancel = () => { this.touchStart = null; };
      this.onVisibilityChange = () => this.refresh();
      this.onMotionChange = () => this.refresh();

      this.addEventListener('pointerdown', this.onPointerDown, {passive: true});
      this.addEventListener('pointerup', this.onPointerUp, {passive: true});
      this.addEventListener('pointercancel', this.onPointerCancel, {passive: true});
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      this.addEventListener('mouseenter', this.onMouseEnter);
      this.addEventListener('mouseleave', this.onMouseLeave);
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery.addEventListener('change', this.onMotionChange);
      this.hiddenObserver = new MutationObserver(() => this.refresh());
      this.hiddenObserver.observe(this, { attributes: true, attributeFilter: ['hidden'] });
      if ('IntersectionObserver' in window) {
        this.visibilityObserver = new IntersectionObserver(entries => {
          const entry = entries.find(item => item.target === this);
          if (!entry) return;
          this.visible = entry.isIntersecting;
          this.refresh();
        });
        this.visibilityObserver.observe(this);
      }
      this.render();
      this.refresh();
    }

    disconnectedCallback() {
      if (!this.running) return;
      this.running = false;
      window.clearTimeout(this.timer);
      this.timer = null;
      this.hiddenObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.removeEventListener('pointerdown', this.onPointerDown);
      this.removeEventListener('pointerup', this.onPointerUp);
      this.removeEventListener('pointercancel', this.onPointerCancel);
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('keydown', this.onKeydown);
      this.removeEventListener('mouseenter', this.onMouseEnter);
      this.removeEventListener('mouseleave', this.onMouseLeave);
      this.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.motionQuery.removeEventListener('change', this.onMotionChange);
    }

    canRotate() {
      return this.running && this.slides.length > 1 && !this.userPaused && !this.motionQuery.matches && !this.hovered && !this.focused && !document.hidden && !this.hidden && this.visible;
    }

    refresh() {
      window.clearTimeout(this.timer);
      this.timer = null;
      if (!this.running) return;
      if (this.toggleButton) {
        const enabled = !this.userPaused && !this.motionQuery.matches;
        const label = enabled ? this.toggleButton.dataset.pauseLabel : this.toggleButton.dataset.playLabel;
        this.toggleButton.textContent = label;
        this.toggleButton.setAttribute('aria-label', label);
        this.toggleButton.setAttribute('aria-pressed', String(enabled));
        this.toggleButton.disabled = this.motionQuery.matches || this.slides.length < 2;
      }
      if (!this.canRotate()) return;
      this.timer = window.setTimeout(() => {
        this.timer = null;
        if (!this.canRotate()) return;
        this.currentIndex = (this.currentIndex + 1) % this.slides.length;
        this.render();
        this.refresh();
      }, this.intervalMs);
    }

    navigate(index) {
      if (!Number.isInteger(index)) return;
      this.userPaused = true;
      this.currentIndex = ((index % this.slides.length) + this.slides.length) % this.slides.length;
      this.render();
      this.refresh();
    }

    render() {
      this.slides.forEach((slide, index) => {
        const active = index === this.currentIndex;
        slide.hidden = !active;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.indexButtons.forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.heroIndex) === this.currentIndex));
      });
    }
  }

  customElements.define('evilko-home-slider', EvilkoHomeSlider);
})();

